import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Switch from "@material-ui/core/Switch";

import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import HistoryIcon from "@material-ui/icons/History";
import SendIcon from "@material-ui/icons/Send";
import FileCopyIcon from "@material-ui/icons/FileCopyOutlined";
import SettingsEthernetIcon from "@material-ui/icons/SettingsEthernet";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import ConfirmationModal from "../../components/ConfirmationModal";
import copyToClipboard from "../../utils/copyToClipboard";

const useStyles = makeStyles((theme) => ({
  url: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    color: theme.palette.text.secondary,
    wordBreak: "break-all",
  },
  events: { display: "flex", flexWrap: "wrap", gap: 4 },
  muted: { color: theme.palette.text.hint },
  secretBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.warning.main}`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(180,83,9,0.12)" : "#FFFBEB",
  },
  secretValue: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    wordBreak: "break-all",
    marginTop: 8,
    display: "block",
  },
  eventGrid: { maxHeight: 260, overflowY: "auto", marginTop: 8 },
  delivery: {
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  deliveryMeta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
}));

const DELIVERY_TONE = {
  success: "success",
  pending: "warning",
  failed: "danger",
  dropped: "neutral",
};

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : null;

const OutboundWebhooks = () => {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", events: [] });
  const [saving, setSaving] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deliveriesOf, setDeliveriesOf] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/outbound-webhooks");
      setRows(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
    api.get("/outbound-webhooks/events")
      .then((r) => setEvents(r.data || []))
      .catch(() => setEvents([]));
  }, [fetchRows]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/outbound-webhooks", form);
      // O segredo só volta aqui, uma vez. Fica em destaque até o usuário fechar.
      setNewSecret({ name: data.name, secret: data.secret });
      setModalOpen(false);
      setForm({ name: "", url: "", events: [] });
      fetchRows();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row) => {
    try {
      await api.put(`/outbound-webhooks/${row.id}`, { active: !row.active });
      fetchRows();
    } catch (err) {
      toastError(err);
    }
  };

  const handleTest = async (row) => {
    try {
      await api.post(`/outbound-webhooks/${row.id}/test`, {});
      toast.success("Evento de teste enfileirado. Chega em até 10 segundos.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/outbound-webhooks/${deleting.id}`);
      toast.success("Webhook removido");
      fetchRows();
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  const openDeliveries = async (row) => {
    setDeliveriesOf(row);
    try {
      const { data } = await api.get(`/outbound-webhooks/${row.id}/deliveries`);
      setDeliveries(data || []);
    } catch (err) {
      toastError(err);
      setDeliveries([]);
    }
  };

  const toggleEvent = (ev) =>
    setForm((prev) => ({
      ...prev,
      events: prev.events.includes(ev)
        ? prev.events.filter((e) => e !== ev)
        : [...prev.events, ev],
    }));

  const columns = [
    {
      key: "name",
      label: "Destino",
      render: (r) => (
        <div>
          <Typography variant="subtitle2">{r.name}</Typography>
          <span className={classes.url}>{r.url}</span>
        </div>
      ),
    },
    {
      key: "events",
      label: "Eventos",
      render: (r) => (
        <div className={classes.events}>
          {(r.events || []).includes("*") ? (
            <StatusChip tone="info" label="Todos" />
          ) : (
            (r.events || []).slice(0, 3).map((e) => (
              <StatusChip key={e} tone="neutral" label={e} />
            ))
          )}
          {(r.events || []).length > 3 && !(r.events || []).includes("*") && (
            <StatusChip tone="neutral" label={`+${r.events.length - 3}`} />
          )}
        </div>
      ),
    },
    {
      key: "lastDeliveryAt",
      label: "Última entrega",
      render: (r) =>
        r.lastDeliveryAt ? (
          <div>
            <div>{fmtDateTime(r.lastDeliveryAt)}</div>
            <Typography variant="caption" className={classes.deliveryMeta}>
              HTTP {r.lastStatus || "—"}
              {r.consecutiveFailures > 0 ? ` · ${r.consecutiveFailures} falhas seguidas` : ""}
            </Typography>
          </div>
        ) : (
          <span className={classes.muted}>Nunca</span>
        ),
    },
    {
      key: "active",
      label: "Ativo",
      render: (r) => (
        <Tooltip
          title={
            r.active
              ? "Desativar"
              : "Reativar — o contador de falhas volta a zero"
          }
        >
          <Switch size="small" checked={!!r.active} onChange={() => handleToggle(r)} />
        </Tooltip>
      ),
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (r) => (
        <>
          <Tooltip title="Enviar evento de teste">
            <IconButton size="small" onClick={() => handleTest(r)}>
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Histórico de entregas">
            <IconButton size="small" onClick={() => openDeliveries(r)}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remover">
            <IconButton size="small" onClick={() => setDeleting(r)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? `Remover o webhook "${deleting.name}"?` : ""}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        O destino deixa de receber eventos imediatamente. O histórico de entregas
        também é removido.
      </ConfirmationModal>

      <PageHeader
        title="Webhooks de saída"
        count={rows.length}
        subtitle="Envie eventos do CRM para n8n, Zapier ou qualquer endpoint HTTP. Cada requisição vai assinada com HMAC-SHA256."
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            Novo webhook
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon={<SettingsEthernetIcon />}
        emptyTitle="Nenhum webhook configurado"
        emptyDescription="Cadastre um endpoint para receber eventos como negócio ganho, proposta aceita ou pagamento recebido."
        emptyAction={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            Novo webhook
          </Button>
        }
      />

      {/* Criação */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo webhook</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Nome *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="URL de destino *"
                placeholder="https://n8n.exemplo.com/webhook/crm"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                helperText="Endereços internos exigem WEBHOOK_ALLOW_PRIVATE_TARGETS no servidor."
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Eventos</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.events.includes("*")}
                    onChange={() => toggleEvent("*")}
                  />
                }
                label="Todos os eventos"
              />
              {!form.events.includes("*") && (
                <Grid container className={classes.eventGrid}>
                  {events.map((ev) => (
                    <Grid item xs={12} sm={6} key={ev}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={form.events.includes(ev)}
                            onChange={() => toggleEvent(ev)}
                          />
                        }
                        label={<span style={{ fontSize: 13 }}>{ev}</span>}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.url.trim() || !form.events.length}
          >
            {saving ? "Criando…" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Segredo — aparece uma única vez */}
      <Dialog open={!!newSecret} onClose={() => setNewSecret(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Webhook criado</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Guarde o segredo agora. Ele é usado para validar a assinatura HMAC das
            requisições e <strong>não será exibido novamente</strong>.
          </Typography>
          <div className={classes.secretBox}>
            <Typography variant="caption" style={{ fontWeight: 600 }}>
              Segredo de {newSecret?.name}
            </Typography>
            <code className={classes.secretValue}>{newSecret?.secret}</code>
            <Button
              size="small"
              startIcon={<FileCopyIcon />}
              style={{ marginTop: 8 }}
              onClick={() => {
                copyToClipboard(newSecret.secret);
                toast.success("Segredo copiado");
              }}
            >
              Copiar
            </Button>
          </div>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 16 }}>
            A assinatura chega em <code>X-Aticket-Signature</code> como{" "}
            <code>sha256=HMAC(segredo, &quot;timestamp.corpo&quot;)</code>, com o timestamp em{" "}
            <code>X-Aticket-Timestamp</code>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => setNewSecret(null)}>
            Guardei o segredo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Histórico */}
      <Dialog
        open={!!deliveriesOf}
        onClose={() => setDeliveriesOf(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Entregas — {deliveriesOf?.name}</DialogTitle>
        <DialogContent>
          {!deliveries.length && (
            <Typography variant="body2" color="textSecondary">
              Nenhuma entrega registrada ainda.
            </Typography>
          )}
          {deliveries.map((d) => (
            <div key={d.id} className={classes.delivery}>
              <div>
                <Typography variant="subtitle2">{d.event}</Typography>
                <span className={classes.deliveryMeta}>
                  {fmtDateTime(d.createdAt)} · {d.attempts}{" "}
                  {d.attempts === 1 ? "tentativa" : "tentativas"}
                  {d.statusCode ? ` · HTTP ${d.statusCode}` : ""}
                  {d.errorMessage ? ` · ${d.errorMessage.slice(0, 60)}` : ""}
                </span>
              </div>
              <StatusChip
                dot
                tone={DELIVERY_TONE[d.status] || "neutral"}
                label={d.status}
              />
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveriesOf(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default OutboundWebhooks;
